import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { CAMPI_COMPILA, corpoCompila, valoriCompila } from '../../utils/compila';
import { dominio } from '../../utils/percorso';

// "Compila dati" di un annuncio che l'estrattore non ha letto (stato "da_compilare"): i campi da riempire a mano,
// più l'agenzia (una dell'elenco o una nuova) e la foto. Salvati titolo e prezzo l'immobile è "completato".
// Un annuncio letto dall'estrattore è già completo: non c'è più niente da confermare, si corregge da "Modifica".

// Agenzia dell'immobile: si cerca nell'elenco; se non c'è, il nome scritto diventa un'agenzia nuova
const SceltaAgenzia = ({ valore, onCambia }) => {
  const [testo, setTesto] = useState(valore.nome || '');
  const [trovate, setTrovate] = useState([]);
  useEffect(() => {
    const t = testo.trim();
    if (t.length < 2 || (valore.id && t === valore.nome)) { setTrovate([]); return undefined; }
    const timer = setTimeout(() => {
      axios.get(`${API}/agenzie`, { params: { search: t } }).then((r) => setTrovate((r.data || []).slice(0, 6))).catch(() => setTrovate([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [testo, valore.id, valore.nome]);
  return (
    <div className="campo-cf largo" data-testid="campo-agenzia">
      <label htmlFor="cf-agenzia">Agenzia<small className={`fonte ${valore.id ? 'ok' : valore.nome ? 'prima' : 'manca'}`}>{valore.id ? "dall'elenco" : valore.nome ? 'nuova' : 'da scegliere'}</small></label>
      <input id="cf-agenzia" value={testo} autoComplete="off" placeholder="Nome dell'agenzia (cercatela nell'elenco)"
        onChange={(e) => { setTesto(e.target.value); onCambia({ id: null, nome: e.target.value }); }} />
      {trovate.length > 0 && (
        <div className="agenzie-trovate" role="listbox" aria-label="Agenzie trovate">
          {trovate.map((a) => (
            <button type="button" role="option" aria-selected={valore.id === a.id} key={a.id}
              onClick={() => { setTesto(a.nome); setTrovate([]); onCambia({ id: a.id, nome: a.nome }); }}>
              <b>{a.nome}</b><span>{[a.marchio, a.sede?.comune || a.comune, a.telefono].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CompilaDati = ({ immobile, onChiudi, onCompilato }) => {
  const [valori, setValori] = useState(() => valoriCompila(immobile));
  const [attesa, setAttesa] = useState(false);
  const [agenzia, setAgenzia] = useState({ id: immobile.agenzia_id || null, nome: immobile.agenzia_info?.nome || immobile.agenzia || '' });
  const [foto, setFoto] = useState(immobile.immagine_url || null);
  const [carica, setCarica] = useState(false);
  const caricaFoto = async (file) => {
    if (!file) return;
    setCarica(true);
    try {
      const dati = new FormData();
      dati.append('file', file);
      const r = await axios.post(`${API}/immobili/${immobile.id}/upload-immagine`, dati, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFoto(r.data.immagine_url);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Foto non caricata');
    } finally { setCarica(false); }
  };
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onChiudi(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onChiudi]);

  const salva = async () => {
    setAttesa(true);
    try {
      const corpo = corpoCompila(valori);
      if (agenzia.id && agenzia.id !== immobile.agenzia_id) corpo.agenzia_id = agenzia.id;
      else if (!agenzia.id && agenzia.nome.trim()) corpo.agenzia = agenzia.nome.trim();
      await axios.put(`${API}/immobili/${immobile.id}/compila`, corpo);
      toast.success('Dati salvati: il cliente vede la scheda completa');
      onCompilato();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Dati non salvati');
      setAttesa(false);
    }
  };

  return createPortal(
    <div className="st st-modale compila-modale" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
      <form className="scatola" role="dialog" aria-modal="true" aria-labelledby="compila-titolo" data-testid="compila-dati"
        onSubmit={(e) => { e.preventDefault(); salva(); }}>
        <div className="testa">
          <div style={{ minWidth: 0 }}>
            <span className="k">Da compilare · l'estrattore non l'ha letto · {dominio(immobile.link || '') || 'annuncio'}</span>
            <h3 id="compila-titolo">Compila i dati</h3>
            <p>Copiate i dati dalla pagina dell'annuncio: titolo e prezzo bastano per mostrarlo al cliente.</p>
          </div>
          <button type="button" className="x" onClick={onChiudi} aria-label="Chiudi">✕</button>
        </div>
        <div className="bottoni">
          <button type="submit" className="btn sm p" disabled={attesa || carica}>{attesa ? 'Salvo…' : 'Salva i dati'}</button>
          {immobile.link && <a className="btn sm" href={immobile.link} target="_blank" rel="noopener noreferrer">Apri l'annuncio ↗</a>}
        </div>
        <div className="foto-compila">
          {foto ? <img src={foto} alt="" /> : <span className="vuota" aria-hidden="true">foto</span>}
          <label className="btn sm">{carica ? 'Carico…' : foto ? 'Cambia la foto' : 'Carica una foto'}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden disabled={carica}
              onChange={(e) => caricaFoto(e.target.files?.[0])} aria-label="Foto dell'immobile" />
          </label>
        </div>
        <div className="campi">
          <SceltaAgenzia valore={agenzia} onCambia={setAgenzia} />
          {CAMPI_COMPILA.map(([campo, etichetta, tipo]) => {
            const props = { id: `cf-${campo}`, value: valori[campo], onChange: (e) => setValori((v) => ({ ...v, [campo]: e.target.value })) };
            return (
              <div key={campo} className={`campo-cf${tipo === 'area' || campo === 'titolo' ? ' largo' : ''}`} data-testid={`campo-${campo}`}>
                <label htmlFor={`cf-${campo}`}>{etichetta}</label>
                {tipo === 'area' ? <textarea rows={3} {...props} />
                  : tipo === 'si_no' ? (
                    <select {...props}><option value="">—</option><option value="si">sì</option><option value="no">no</option></select>
                  ) : <input {...props} inputMode={tipo === 'numero' ? 'decimal' : undefined} autoComplete="off" />}
              </div>
            );
          })}
        </div>
      </form>
    </div>,
    document.querySelector('.mk-staff') || document.body,
  );
};

export default CompilaDati;
